import { useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { Divider, Image, Stack, Text, Accordion, Group } from "@mantine/core";
import { Carousel } from "@mantine/carousel";
import { getAssetUrl } from "@utils/getAssetUrl";
import { useAppNavigate } from "@/apps/global/navigate/navigate";
import { mockProduct } from "./mockData";
import { ProductResponseDTO, UserApi } from "@utils/api/ec";
import { ReservationPopup } from "./components";
import { handleProductShareFriend, handleSendOaMessage } from "./utils";
import liff from "@line/liff";
import { useLoadingStore } from "@/stores/useLoadingStores";
import { useUserEvent } from "@/apps/@hooks/useUserEvent";
import { useChooseStoreOpen } from "@/apps/liff/pages/ec-page/hook/useChooseStoreOpen";
import { v4 as uuidv4 } from "uuid";
import { useWaitingInPage } from "@/apps/@hooks/useWaitingInPage";

export default function EcProductPage() {
	const { navigateToEcHome } = useAppNavigate();
	const { id } = useParams();
	const productId = Number(id);
	const [searchParams] = useSearchParams();
	const trackingId = searchParams.get("td");
	const { logEvent, logPage } = useUserEvent();
	const { setChooseStoreOpen, setChooseStoreStyleCode, setChooseStoreType } =
		useChooseStoreOpen();
	const [product, setProduct] = useState<ProductResponseDTO>(
		mockProduct as ProductResponseDTO,
	);
	const [opened, setOpened] = useState(false);
	const [collectionStatus, setCollectionStatus] = useState<boolean>(
		product.collection_status,
	);
	const [isToggling, setIsToggling] = useState(false);
	const { isLoading, setIsLoading } = useLoadingStore();
	const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));
	const [isPageReady, setIsPageReady] = useState(false);

	const handleToggleCollection = async () => {
		if (isToggling) {
			return;
		}
		const nextStatus = !collectionStatus;
		try {
			setIsToggling(true);
			await UserApi.toggleProductCollection(productId, nextStatus);
			setCollectionStatus(nextStatus);
			if (nextStatus) {
				logEvent({
					category: "cta",
					path: `/product/item_id/${productId}`,
					action: "like",
					event: "cta_like",
					value: productId.toString(),
				});
			} else {
				logEvent({
					category: "cta",
					path: `/product/item_id/${productId}`,
					action: "unlike",
					event: "cta_unlike",
					value: productId.toString(),
				});
			}
		} catch (error) {
			console.error("切換收藏狀態失敗", error);
		} finally {
			setIsToggling(false);
		}
	};
	const handleBuyer = async () => {
		setChooseStoreStyleCode(product.style_code);
		setChooseStoreType("buyer");
		return handleSendOaMessage({
			isLoading,
			setIsLoading,
			logEvent,
			log: {
				category: "cta",
				path: `/product/item_id/${productId}`,
				action: "buy",
				event: "cta_buy",
			},
			styleCode: product.style_code,
			type: "buyer",
			onSuccess: () => setOpened(true),
			setIsChooseStoreOpen: setChooseStoreOpen,
		});
	};
	const handleBuyerClose = () => {
		liff.openWindow({
			url: process.env.REACT_APP_LINE_OA_URL ?? "",
			external: false,
		});
	};
	const getProduct = async () => {
		if (!id) {
			navigateToEcHome();
			return;
		}
		setIsLoading(true);

		try {
			const [res] = await Promise.all([
				UserApi.getProduct(Number(id)),
				sleep(1500),
			]);
			if (!res.data) {
				navigateToEcHome();
				return;
			}
			setProduct(res.data);
		} catch (error) {
			console.error("取得商品失敗", error);
		} finally {
			setIsPageReady(true);
			setIsLoading(false);
		}
	};

	useEffect(() => {
		setCollectionStatus(product.collection_status);
	}, [product.collection_status]);

	useEffect(() => {
		getProduct();
	}, [id]);

	useEffect(() => {
		logPage({
			path: `/product/item_id/${productId}`,
		});
	}, []);

	useEffect(() => {
		if (!trackingId) {
			return;
		}
		logEvent({
			category: "mgm",
			path: `/product/item_id/${productId}`,
			action: "flex_item_share_friend_clicked",
			event: "mgm_share_friend_clicked",
			value: trackingId,
		});
	}, [trackingId]);

	useWaitingInPage({
		enabled: isPageReady, // true / false
		getPath: () => `/product/item_id/${productId}`,
		logEvent,
	});

	return (
		<Stack gap="0" flex={1}>
			<>
				<Group ml="3.49vw" gap="1.29vw" my="3.6vw">
					<Text size="3vw">{product.category_name} /</Text>
					<Text size="3vw">{product.name}</Text>
				</Group>
				<Stack pos="relative" gap="5.41vw">
					<Image
						src={getAssetUrl(
							collectionStatus
								? "/images/ec-campaign/active-love-icon.png"
								: "/images/ec-campaign/love-icon.png",
						)}
						alt="love"
						pos="absolute"
						h="3.61vw"
						w="4.12vw"
						right="3.1vw"
						top="3.1vw"
						style={{
							zIndex: 1,
						}}
						onClick={(e) => {
							e.stopPropagation();
							handleToggleCollection();
						}}
					/>
					<Carousel
						withIndicators
						withControls={false}
						classNames={{ indicator: "indicator" }}
					>
						{product.images_url.map((url, idx) => (
							<Carousel.Slide key={idx}>
								<Image src={url} alt="carousel-image" h="109.96vw" />
							</Carousel.Slide>
						))}
					</Carousel>

					<Stack gap="0" mx="4.12vw">
						<Stack gap="1.29vw" mx="4.12vw">
							<Stack gap="2.7vw">
								<Text
									size="3.61vw"
									fw="700"
									style={{ fontFamily: '"Noto Sans TC", sans-serif' }}
								>
									{product.name}
								</Text>
								<Text c="#1A1A1A" size="3.35vw">
									${product.price.toLocaleString()}
								</Text>
							</Stack>
							<Text size="3.09vw" mt="5vw">
								顏色：{product.color}
							</Text>
						</Stack>
						<Divider c="#E2E4E7" my="5.15vw" />
						<Stack gap="2.58vw">
							<Image
								src={getAssetUrl(
									"/images/ec-campaign/product-page/buy-btn.png",
								)}
								alt="add-to-cart-icon"
								h="9.06vw"
								w="100%"
								onClick={handleBuyer}
							/>
							<Image
								src={getAssetUrl(
									"/images/ec-campaign/product-page/share-btn.png",
								)}
								alt="share-icon"
								h="9.03vw"
								onClick={() => {
									let shareTracking = uuidv4().toString();
									logEvent({
										category: "cta",
										path: `/productlist/category_id/${id}`,
										action: "share_item",
										event: "cta_share_item",
									});
									logEvent({
										category: "mgm",
										path: `/productlist/category_id/${id}`,
										action: "flex_item_share",
										event: "mgm_share",
										value: shareTracking,
									});
									handleProductShareFriend(
										product.name,
										productId,
										product.images_url.length > 0
											? product.images_url[0]
											: getAssetUrl(
													"/images/ec-campaign/flex-message/product-content.png",
												),
										shareTracking,
									);
								}}
							/>
						</Stack>
						<Divider c="#E2E4E7" mt="5.15vw" />
						<Accordion
							variant="contained"
							styles={{
								control: { padding: "3.09vw 0" },
								label: { fontSize: "3.09vw" },
								item: { border: "none" },
							}}
						>
							<Accordion.Item value="desc" bg="white">
								<Accordion.Control>商品說明</Accordion.Control>
								<Accordion.Panel>
									<Text c="#2E3238" size="3.09vw" lh="1.5">
										{product.description}
									</Text>
								</Accordion.Panel>
							</Accordion.Item>
						</Accordion>
						<Divider c="#E2E4E7" />
						<Accordion
							variant="contained"
							styles={{
								control: { padding: "3.09vw 0" },
								label: { fontSize: "3.09vw" },
								item: { border: "none" },
							}}
						>
							<Accordion.Item value="desc" bg="white">
								<Accordion.Control>商品詳情</Accordion.Control>
								<Accordion.Panel>
									<Stack gap="1.55vw">
										{product.details.map((d, idx) => (
											<Text key={idx} size="3.09vw">
												{d}
											</Text>
										))}
									</Stack>
								</Accordion.Panel>
							</Accordion.Item>
						</Accordion>
						<Divider c="#E2E4E7" />
					</Stack>
				</Stack>
				<ReservationPopup
					opened={opened}
					onClose={() => setOpened(false)}
					onConfirm={handleBuyerClose}
				/>
			</>
		</Stack>
	);
}
